import { useState } from 'react'; // Removed useEffect/location since no token_hash
import { SUPPORT_TICKET_URL, TERMS_URL, PRIVACY_URL } from '@/config/urls';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FormEvent } from 'react';

export default function ResetPassword() {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleVerifyOtp = async (e: FormEvent) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setIsSubmitting(true);

    try {
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'recovery',
      });

      if (verifyError) {
        console.error('OTP verification error:', verifyError);
        setError(
          verifyError.message ||
            'Invalid or expired code. Please try again or request a new one.'
        );
        return;
      }

      if (data.session) {
        setIsVerified(true);
        setMessage('Code verified! Set your new password.');
      } else {
        setError('Unable to verify code. Please try again.');
      }
    } catch (err) {
      console.error('Unexpected verification error:', err);
      setError('An unexpected error occurred during verification.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdatePassword = async (e: FormEvent) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setIsSubmitting(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        setError(updateError.message);
        return;
      }

      setMessage('Password updated successfully! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle support link click
  const handleSupportLinkClick = () => {
    window.open(SUPPORT_TICKET_URL, '_blank');
    setError(''); // Clear error
    window.location.reload(); // Refresh page
  };

  return (
    <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/30 to-background p-4'>
      <div className='w-full max-w-md space-y-8'>
        {/* Logo and Header */}
        <div className='text-center space-y-4'>
          <div className='flex items-center justify-center'>
            <img
              src='/images/8e8b8d0c-2b0f-47d3-aed3-98e19c548c8d.png'
              alt='CallSparker'
              className='h-16'
            />
          </div>
          <p className='text-muted-foreground'>
            Power your B2B voice prospecting with AI
          </p>
        </div>
        {/* Password Reset Form */}
        <Card className='border-2'>
          <CardHeader className='space-y-1'>
            <CardTitle className='text-2xl font-semibold'>
              Reset Password
            </CardTitle>
            <CardDescription>
              Enter your email and the 6-digit code we sent to your inbox. Check your spam folder if you don't see it.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {message && (
              <Alert variant='default' className='mb-4'>
                <AlertDescription>{message}</AlertDescription>
              </Alert>
            )}
            {error && (
              <Alert variant='destructive' className='mb-4'>
                <AlertDescription>
                  {error === 'User is banned' ? (
                    <p>
                      Your account has been disabled. Please{' '}
                      <button
                        type='button'
                        onClick={handleSupportLinkClick}
                        className='text-blue-500 underline hover:text-blue-700'
                      >
                        contact support
                      </button>{' '}
                      for assistance.
                    </p>
                  ) : (
                    error
                  )}
                </AlertDescription>
              </Alert>
            )}
            {!isVerified ? (
              <form onSubmit={handleVerifyOtp} className='space-y-4'>
                <div className='space-y-2'>
                  <Label htmlFor='email'>Email</Label>
                  <Input
                    id='email'
                    type='email'
                    placeholder='your@email.com'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className='h-11'
                    disabled={isSubmitting}
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='otp'>6-digit code from your email</Label>
                  <Input
                    id='otp'
                    type='text'
                    inputMode='numeric'
                    placeholder='123456'
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                    className='h-11'
                    disabled={isSubmitting}
                  />
                </div>
                <Button
                  type='submit'
                  className='w-full h-11'
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Verifying...' : 'Verify Code'}
                </Button>
                <p className='text-center text-sm text-muted-foreground'>
                  Didn't get a code?&nbsp;
                  <Link to='/forgot-password' className='text-blue-500 hover:underline'>
                    Resend
                  </Link>
                </p>
              </form>
            ) : (
              <form onSubmit={handleUpdatePassword} className='space-y-4'>
                <div className='space-y-2'>
                  <Label htmlFor='new-password'>New Password</Label>
                  <Input
                    id='new-password'
                    type='password'
                    placeholder='Enter new password'
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className='h-11'
                    disabled={isSubmitting}
                  />
                </div>
                <Button
                  type='submit'
                  className='w-full h-11'
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Updating...' : 'Update Password'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Legal Links */}
        <p className='text-center text-xs text-muted-foreground'>
          By using this service, you agree to our{' '}
          <a
            href={TERMS_URL}
            target='_blank'
            rel='noopener noreferrer'
            className='underline hover:text-foreground'
          >
            Terms of Service
          </a>{' '}
          and{' '}
          <a
            href={PRIVACY_URL}
            target='_blank'
            rel='noopener noreferrer'
            className='underline hover:text-foreground'
          >
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  );
}
